<template>
  <section>
    <b-button @click="$bvModal.show(modalId)" size="sm" variant="primary" class="mr-2">
      <i class="fa fa-plus" aria-hidden="true"></i>
      Apply Credit
    </b-button>
    <b-modal size="lg" :id="modalId" :ref="modalId" @show="resetModal(true)">
      <template slot="modal-header">
        <h5 class="modal-title">Apply Credit</h5>
      </template>
      <div>
        <b-row class="mb-2">
          <b-col cols="2">
            <b-form-group label="Customer">
              <label>{{details.companyName}}</label>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Sales Order No">
              <label>{{details.salesOrderId}}</label>
            </b-form-group>
          </b-col>
          <template v-if="isInvoicePage()">
            <b-col cols="1">
              <b-form-group label="Invoice No">
                <label>{{details.invoiceNumber}}</label>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Invoice Date">
                <label>{{date(details.invoiceDate)}}</label>
              </b-form-group>
            </b-col>
          </template>
          <template v-if="isPoPage()">
            <b-col cols="1">
              <b-form-group label="Purchase Orders No">
                <label>{{details.id}}</label>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Purchase Order Date">
                <label>{{date(details.poDate)}}</label>
              </b-form-group>
            </b-col>
          </template>
          <template v-if="isServiceOrderPage()">
            <b-col cols="1">
              <b-form-group label="Work Order #">
                <label>{{details.id}}</label>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Work Order Date">
                <label>{{date(details.soDate)}}</label>
              </b-form-group>
            </b-col>
          </template>
          <b-col cols="1">
            <b-form-group label="Amount">
              <label>{{ Number(details.amountPaid).toLocaleString('en-US', { style: 'currency', currency: 'USD',
                minimumFractionDigits: 2 }) }}</label>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Balance">
              <label>{{ Number(details.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD',
                minimumFractionDigits: 2 }) }}</label>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Applied">
              <label>{{ Number(totalUsedAmount).toLocaleString('en-US', { style: 'currency', currency: 'USD',
                minimumFractionDigits: 2 }) }}</label>
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group label="New Balance">
              <label>{{ Number(totalRemainAmount).toLocaleString('en-US', { style: 'currency', currency: 'USD',
                minimumFractionDigits: 2 }) }}</label>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- <p class="mt-2">Select the credits to apply to the invoice. If there is credit that is tied to a sales order, it
          must be used first.</p> -->
        <b-alert v-if="isGettingDetails" variant="info" show style="text-align: center"><strong>Getting Credit Details...</strong></b-alert>
        <Grid
          class="grid"
          :resizable="false"
          :reorderable="false"
          :sortable="false"
          :row-height="25"
          selected-field="selected"
          :data-items="creditItemData"
          :columns="prepareColumnsData({ initialColumns: creditFields, settingsColumns: gridSettings.columns })"
        >
          <template v-slot:selectCommandsHeader="{}">
            <div class="d-flex justify-content-center">
              <grid-settings :gridId="creditGridId" :fields="creditFields" class="mr-2"/>
            </div>
          </template>
          <template v-slot:selectcommands="{props}">
            <td>
              <div class="d-flex justify-content-center align-items-center">
                <input
                  type="checkbox"
                  v-model="checkedCreditArr[props.dataItem.index].selected"
                  :disabled="checkedCreditArr[props.dataItem.index].disabled"
                  @change="onSetCheckedCreditRow($event, props.dataItem)"
                />
              </div>
            </td>
          </template>
          <template v-slot:editPayment="{props}">
            <td>
              <div class="d-flex align-items-center">
                <input
                  type="number"
                  class="amountTextBox"
                  v-model="checkedCreditArr[props.dataItem.index].payment"
                  :disabled="checkedCreditArr[props.dataItem.index].disabled"
                  @change="onSetCreditAmount($event, props.dataItem)"
                />
              </div>
            </td>
          </template>
          <template v-slot:formatCurrency="{props}">
            <td>
              {{ Number(props.dataItem.amount).toLocaleString('en-US', { style: 'currency', currency: 'USD',
              minimumFractionDigits: 2 }) }}
            </td>
          </template>
          <template v-slot:balance="{props}">
            <td>
              {{ Number(props.dataItem.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD',
              minimumFractionDigits: 2 }) }}
            </td>
          </template>
          <template v-slot:formatDate="{props}">
            <td>{{ props.dataItem.dateCreated | date }}</td>
          </template>
        </Grid>
      </div>
      <template slot="modal-footer">
        <b-button @click="$bvModal.hide(modalId)">Cancel</b-button>
        <b-button variant="primary" :disabled="isSaving" @click="saveCredits"><i v-show="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Apply Credit</b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { GRID_ID } from '@/shared/constants';
  import creditFields from './fields';
  import { date } from '@/shared/filters';
  import { prepareColumnsData } from '@/shared/helpers';
  import GridSettings from '@/shared/components/GridSettings';

  export default {
    components: {
      GridSettings,
    },
    props: {
      details: {
        type: Object,
        default: () => {
        },
      },
      page: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        creditFields,
        prepareColumnsData,
        modalId: 'apply-credits-modal',
        creditGridId: GRID_ID.APPLY_CREDITMEMOS,
        totalReceivedAmount: this.details.balance,
        totalUsedAmount: 0,
        totalRemainAmount: 0,
        checkedCreditArr: [],
        creditMemosList: [],
        date,
        isSaving:false,
        isGettingDetails: false,
      };
    },
    filters: {
      date,
    },
    computed: {
      ...mapGetters('account', ['gridSettingsById']),
      ...mapGetters('serviceOrderLines', ['serviceOrderLinesById']),
      gridSettings() {
        return this.gridSettingsById(this.gridId);
      },
      creditItemData() {
        if (this.details.companyId > 0 && this.creditMemosList.length) {
          //if company is selected then set additional data into list
          this.checkedCreditArr = this.creditMemosList.map((creditData, index) => ({
            ...creditData,
            payment: '', // user will be add credit payment
            index,
            selected: false,
            disabled: false,
          }));
        } else {
          //if company is not available then empty credit list
          this.checkedCreditArr = [];
        }
        return this.checkedCreditArr;
      },
    },
    methods: {
      ...mapActions('account', ['saveGridSettings']),
      ...mapActions('creditMemos', ['getCreditsByCompany']),
      ...mapActions('invoices', ['applyInvoiceCredit']),
      ...mapActions('purchaseorders', ['applyPurchaseOrderCredit']),
      ...mapActions('serviceOrders', ['applyServiceOrderCredit']),
      //when modal load below function is call and get credit memos by company id
      isInvoicePage(){
        return (this.page=='invoice-page')
      },
      isPoPage(){
        return (this.page=='purchase-order')
      },
      isServiceOrderPage(){
        return (this.page=='service-order')
      },
      async resetModal(getData = false) {
        //when modal open clear table list
        this.creditMemosList = [];
        if (getData && this.details.companyId) {
          this.isGettingDetails = true;
          this.creditMemosList = await this.getCreditsByCompany(this.details.companyId);
          this.isGettingDetails = false;
          this.$emit('credit-result', this.creditMemosList);
          //console.log('company credit data',this.creditMemosList[0]);
        }
      },
      onClear() {
        //clear selected rows and balance
        this.totalReceivedAmount =  this.totalUsedAmount = this.totalRemainAmount = 0;
      },
      onSetAmount() {
        //when data are load into table then update rows based on sales order id
        let availAmount = this.totalReceivedAmount;
        //if we have balance in credit or received payment or expenses then proceed
        if (availAmount > 0) {
          //check each selected row balance and calculate payment
          if (this.checkedCreditArr.length) {
            this.checkedCreditArr.map((data, index) => {
              //here again we check availTotal because on each row we deduct amount and make sure loop will execute till balance is zero
              // if invoice sales order and credit memo sales order are matched then mark as selected and disable radio button and textbox
              if (availAmount > 0 && this.checkedCreditArr[index].salesOrderId && this.details.salesOrderId && this.checkedCreditArr[index].salesOrderId == this.details.salesOrderId) {
                //check if credit grid payment are enough to set balance then first use credit amount
                if (availAmount >= this.checkedCreditArr[index].balance) {
                  //set credit into this row credit check box and total payment
                  this.checkedCreditArr[index].payment = data.balance;
                  //deduct selected row balance from total available and total credit balance
                  availAmount = parseFloat(availAmount - this.checkedCreditArr[index].balance);
                  this.totalUsedAmount += parseFloat(this.checkedCreditArr[index].balance);
                } else {
                  //if available amount is less than balance then set available amount into amount textbox
                  this.checkedCreditArr[index].payment = availAmount;
                  this.totalUsedAmount += parseFloat(availAmount);
                  availAmount = 0;
                }
                //mark row as selected and disable checkbox and textbox
                this.checkedCreditArr[index].selected = true;
                this.checkedCreditArr[index].disabled = true;
              } else {
                //once total available amount become zero then uncheck other rows
                this.checkedCreditArr[index].payment = '';
                this.checkedCreditArr[index].selected = false;
                //if credit memo has sales order id but which is not tied with this invoice then user can't select it so mark as disable
                this.checkedCreditArr[index].disabled = (this.checkedCreditArr[index].salesOrderId) ? true : false;
              }
            });
            //once all row finished then update total balances
            this.updateBalance();
          }
        } else {
          this.onClear();
        }
        return true;
      },
      onSetCheckedCreditRow(value, data) {
        if (this.updateBalance(true)) {
          //this function called when credit grid is checked
          if (!value.target.checked) {
            //value.attr('disabled',true);
            //if checkbox is unchecked then clear amount
            this.checkedCreditArr[data.index].payment = '';
          } else if (this.totalRemainAmount > 0) {
            //if amount is remaining after deduct selected row balance then proceed
            if (this.totalRemainAmount > data.balance) {
              this.checkedCreditArr[data.index].payment = data.balance;
            } else {
              this.checkedCreditArr[data.index].payment = this.totalRemainAmount;
            }
          } else {
            //if remaining amount is not available then remove selection
            value.target.checked = false;
            this.checkedCreditArr[data.index].selected = false;
            this.checkedCreditArr[data.index].payment = '';
          }
          //after credit grid checked again set amount into invoices grid
          this.updateBalance();
        }
      },
      onSetCreditAmount(value, data) {
        //when amount is enter into textbox then make checkbox selected
        if (!this.checkedCreditArr[data.index].selected) {
          this.checkedCreditArr[data.index].selected = true;
        }
        const newVal = parseFloat(value.target.value)

        //first update balance (because amount already added itto textbox) and then
        if (this.updateBalance(true)) {

          //if credit balance is not updated and remaining amount is goes to negative then set balance empty
          if (newVal == '' || isNaN(newVal) || newVal > this.checkedCreditArr[data.index].balance || this.totalRemainAmount < 0) {
            this.checkedCreditArr[data.index].payment = '';
            this.checkedCreditArr[data.index].selected = false;
          }

          //after finished above calculation again update balance
          this.updateBalance();
        }
      },
      updateBalance(needReturn = false) {
        //update all total and final balances
        this.totalUsedAmount = 0;
        if (this.checkedCreditArr.length) {
          this.checkedCreditArr.map(data => {
            if (data.selected && data.payment > 0) {
              this.totalUsedAmount += parseFloat(data.payment);
            }
          });
        }
        this.totalRemainAmount = parseFloat(this.totalReceivedAmount) - parseFloat(this.totalUsedAmount);
        if (needReturn) {
          return true;
        }
      },
      async saveCredits() {
        let creditMemoTransactions = [];
        //map credit memo array
        this.checkedCreditArr.map((data, index) => {
          if (data.selected && data.payment > 0) {
            if (this.details.salesOrderId && data.salesOrderId && this.details.salesOrderId != data.salesOrderId) {
              delete this.checkedCreditArr[index];
            } else {
              creditMemoTransactions.push({
                id: 0,
                creditMemoId: data.id,
                amount: data.payment,
                invoiceId: (this.isInvoicePage()) ? this.details.invoiceNumber:0,
                purchaseOrderId: (this.isPoPage())?this.details.id:0,
                serviceOrderId: (this.isServiceOrderPage())?this.details.id:0,
                active: true,
              });
            }
          }
        });
        if (creditMemoTransactions.length) {
          try {
            this.isSaving = true;
            if (this.isInvoicePage()) {
              await this.applyInvoiceCredit({
                creditMemoTransactions: creditMemoTransactions,
                invoiceId: this.details.invoiceNumber
              })
            } else if (this.isPoPage()) {
              await this.applyPurchaseOrderCredit({
                creditMemoTransactions: creditMemoTransactions,
                purchaseOrderId: this.details.id
              })
            } else if (this.isServiceOrderPage()) {
              await this.applyServiceOrderCredit({
                creditMemoTransactions: creditMemoTransactions,
                serviceOrderId: this.details.id
              })
            }
            this.isSaving = false;
            this.$emit('credit-applied');
            this.onClear();
            this.$bvModal.hide(this.modalId)
          }catch (e) {
            this.isSaving = false;
          }
        }
      }
    },
    watch: {
      checkedCreditArr() {
        //if(this.details.salesOrderId && this.checkedCreditArr.length){
        this.onSetAmount();
        //}
      },
    },
    async mounted() {
      //console.log('apply credit page',this.page,this.details);
      
    },
    beforeDestroy() {

    },
    updated() {
      this.totalReceivedAmount = this.details.balance;
    }
  };
</script>

<style lang="scss">
  #apply-credits-modal .modal-dialog {
    max-width: 992px;
  }
</style>
